<template>
  <div
    class="w-[18.25rem] rounded py-4 px-3 border dark:bg-n-800/80 bg-n-0 dark:border-n-700 border-n-50 dark:backdrop-blur-sm box-border flex align-text-bottom font-body text-sm leading-5"
    :class="{ 'cursor-pointer': onAction }"
    @click="executeAction"
  >
    <div class="flex grow flex-col gap-1">
      <div class="flex gap-2 items-center">
        <div :class="typeClasses" class="rounded-full w-8 h-8 flex items-center justify-center">
          <l-icon :name="selectedIcon" :class="'text-n-0'" size="small" />
        </div>
        <div class="flex-1 gap-2 dark:text-n-0 text-n-800">
          {{ title }}
        </div>
        <l-icon
          v-if="!progress || progress >= 100"
          :class="{
            'cursor-pointer': closeButton,
            'pointer-events-none': !closeButton,
            invisible: !closeButton
          }"
          name="close"
          class="text-v"
          @click.stop="emit('close')"
        />
        <div
          v-else
          class="text-v-300 font-body text-sm leading-5 cursor-pointer"
          @click="emit('cancel')"
        >
          {{ t('notification.cancel') }}
        </div>
      </div>
      <div v-if="description" class="flex gap-2">
        <div class="w-8"></div>
        <div class="flex-1 dark:text-n-200 text-n-300 first-letter:capitalize">
          {{ description }}
        </div>
        <div class="w-5"></div>
      </div>
      <div v-if="hasProgress" class="flex">
        <div class="w-12"></div>
        <div class="w-full h-3 bg-v-100 rounded-full overflow-hidden">
          <div
            :class="typeClasses"
            class="flex items-center justify-center h-3 bg-violet rounded-full transition-all duration-200"
            :style="progressStyles"
          >
            <div v-if="progress && progress >= 20" class="text-center text-n-0 text-xs">
              {{ Math.min(progress, 100) }}%
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="timeAgo && !hasProgress"
        class="flex justify-end font-body text-xs leading-4 dark:text-n-200 text-n-300"
      >
        {{ timeAgo }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import { differenceInSeconds, formatRelative } from 'date-fns'
import { computed, ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

import LIcon from '../LIcon.vue'
import { type LNotificationProps } from './types'

const props = withDefaults(defineProps<LNotificationProps>(), { closeButton: true })

const emit = defineEmits<{
  close: []
  cancel: []
}>()

const { t } = useI18n()

const timeAgo = ref<string | null>(null)
const createdAt = new Date()

const typeClasses = computed(() => {
  switch (props.type) {
    case 'info':
      return ['bg-v-300']
    case 'success':
      return ['bg-g-500']
    case 'error':
      return ['bg-r-500']
    default:
      return ['bg-v-300']
  }
})

const selectedIcon = computed(() => {
  switch (props.type) {
    case 'info':
      return props.icon ?? 'question-circle'
    case 'success':
      return 'check-circle'
    case 'error':
      return 'alert-circle'
    default:
      return props.icon ?? 'question-circle'
  }
})

const progress = computed(() => {
  return toValue(props.progress)
})

const progressStyles = computed(() => {
  if (!props.progress) return
  return {
    width: `${props.progress}%`
  }
})

const hasProgress = computed(() => progress.value !== undefined && progress.value >= 0)

useIntervalFn(() => {
  const initialDiff = differenceInSeconds(new Date(), createdAt)
  if (initialDiff > 60) {
    timeAgo.value = formatRelative(createdAt, new Date())
  }
})

function executeAction() {
  if (props.onAction) {
    props.onAction()
  }
}
</script>
